import React, { useEffect, useState } from 'react';
import {
  getActiveEntraAccount,
  initializeEntraAuth,
  isAllowedDomainUser,
  isEntraConfigured,
  isRunningInsidePopupWindow,
  shouldUseRedirectAuth,
  signInWithMicrosoft,
  signOutFromMicrosoft,
} from '../../lib/entraAuth';
import logo from '../../assets/images/logo.png';

const CrmAuthGate = ({ children }) => {
  const [authReady, setAuthReady] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [authInfo, setAuthInfo] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  const inPopupWindow = isRunningInsidePopupWindow();
  const prefersRedirect = shouldUseRedirectAuth();

  const hasAuthResponseParams = (() => {
    const hash = String(window.location.hash || '').toLowerCase();
    const search = String(window.location.search || '').toLowerCase();
    return [hash, search].some((value) => (
      value.includes('code=') ||
      value.includes('error=') ||
      value.includes('id_token=') ||
      value.includes('state=')
    ));
  })();

  useEffect(() => {
    (async () => {
      try {
        if (inPopupWindow && hasAuthResponseParams) {
          return;
        }

        if (!isEntraConfigured()) {
          setAuthError('Chybí Entra konfigurace (REACT_APP_ENTRA_TENANT_ID, REACT_APP_ENTRA_CLIENT_ID, REACT_APP_ENTRA_API_SCOPE).');
          return;
        }

        const account = await initializeEntraAuth();
        setAuthUser(account || getActiveEntraAccount());
      } catch (e) {
        setAuthError(e instanceof Error ? e.message : 'Nepodařilo se inicializovat Microsoft Entra přihlášení.');
      } finally {
        setAuthReady(true);
      }
    })();
  }, [inPopupWindow, hasAuthResponseParams]);

  const signIn = async () => {
    if (signingIn) return;

    setAuthError(null);
    setAuthInfo('');
    setSigningIn(true);
    try {
      const result = await signInWithMicrosoft();
      if (result && result.redirected) {
        setAuthInfo('Přesměrovávám na Microsoft přihlášení...');
        return;
      }

      const account = result ? result.account : null;
      if (!account) {
        setAuthError('Přihlášení nebylo dokončeno.');
        return;
      }

      if (!isAllowedDomainUser(account)) {
        await signOutFromMicrosoft();
        setAuthError('Přístup je povolen pouze pro firemní e-mail @derator.cz.');
        return;
      }

      setAuthUser(account);
    } catch (e) {
      setAuthError(e instanceof Error ? e.message : 'Přihlášení přes Microsoft se nezdařilo.');
    } finally {
      setSigningIn(false);
    }
  };

  if (!authReady) {
    return (
      <main className="auth-wrap theme-mode-dark theme-color-orange">
        <section className="card auth-card">Načítám aplikaci...</section>
      </main>
    );
  }

  if (!authUser) {
    return (
      <main className="auth-wrap theme-mode-dark theme-color-orange">
        <section className="card auth-card">
          <div className="auth-card__logo-wrap">
            <img src={logo} alt="Derator" className="auth-card__logo" />
          </div>
          <p className="auth-card__subtitle">Přihlášení do CRM</p>

          {inPopupWindow ? (
            <p className="auth-info" style={{ marginTop: 12 }}>
              Dokončuji přihlášení v tomto okně. Pokud se přihlášení nezavře automaticky, zavřete jej a pokračujte v hlavním okně CRM.
            </p>
          ) : (
            <div className="form-grid" style={{ marginTop: 12 }}>
              <button className="btn btn-primary" type="button" onClick={signIn} disabled={signingIn}>
                {signingIn ? 'Přihlašuji...' : 'Přihlásit přes Microsoft'}
              </button>
              {prefersRedirect ? (
                <p className="crm-form-note">
                  V tomto prostředí se přihlášení otevře přesměrováním ve stejném okně, ne v popupu.
                </p>
              ) : null}
            </div>
          )}

          <p className="auth-switch" style={{ marginTop: 14 }}>
            Použijte firemní účet @derator.cz.
          </p>

          {authError ? <p className="auth-error">{authError}</p> : null}
          {authInfo ? <p className="auth-info">{authInfo}</p> : null}
        </section>
      </main>
    );
  }

  const signOut = async () => {
    await signOutFromMicrosoft();
    setAuthUser(null);
  };

  return children({
    email: String(authUser.username || authUser.name || '').trim(),
    user: authUser,
    signOut,
  });
};

export default CrmAuthGate;
